// Каталог площадок (кустов) — CRUD над `/api/public/clusters`. Средний уровень
// онтологии Field→Cluster→Well→Wellbore. По паттерну FieldsPage/WellsPage (list
// + Editor в одном файле, refetch вместо оптимистики, list↔editor через
// search-параметры ?edit=N / ?new=1).
//
// Поля: name (обяз.), description (текст), field_id (привязка к месторождению —
// `<select>` по каталогу /api/public/fields, пусто = «не привязано») и ЯКОРЬ.
//
// 🔑 Якорь — центр площадки (OSDU `WellSiteStructure.SpatialLocation`), точка
// ЦЕЛИКОМ: пара едет вместе со своим датумом, половина точки невыразима типом
// (`AnchorDto` держит пару и датум не-Option), поэтому форма отказывает на
// «широта есть, долготы нет», а не досылает половину. Датум выбирается явно —
// WGS 84 не подставляется (Ш7 docs/osdu/PLAN_earth_point_standard.md). Якорь в
// СЕТКЕ (метры) форма не вводит и не правит — показывает; его вводят в
// приложении. Высоты у якоря нет с Ф5 Ш8.5-д: высота площадки — её отметки.
//
// ⚠️ Снять уже заданный якорь через сайт НЕЛЬЗЯ: в PATCH-теле `reference: null`
// означает «не трогать» (v1, тот же выбор, что у `field`), отдельного
// `clear_reference` сервер не принимает. Форма это говорит вслух, а не молчит:
// очистка координат у записи с якорем — ошибка на submit. Молчаливое «сохранил,
// а якорь остался» было бы ровно тем классом, ради которого объявления и
// заводились.

import { createSignal, createResource, createMemo, Show, For, onMount, onCleanup } from 'solid-js'
import type { Component } from 'solid-js'
import { Icon } from './Icons'
import {
  listClusters,
  createCluster,
  updateCluster,
  deleteCluster,
  InUseError,
} from './api/clusters'
import type { ClusterDto, AnchorDto, CreateClusterBody, UpdateClusterBody } from './api/clusters'
import { listFields } from './api/fields'
import type { FieldDto } from './api/fields'
import type { DatumId } from '@webtypes/DatumId'
import type { DeclaredCrsDto } from '@webtypes/DeclaredCrsDto'
import {
  DATUM_IDS,
  DATUM_NAMES,
  CRS_FAMILY_FULL_NAME_TEMPLATES,
} from '@webtypes/pointRegistry'

// ────────────────────── редактор ──────────────────────

type EditorState = {
  /// `null` — создаём. Иначе редактируем.
  source:        ClusterDto | null
  name:          string
  description:   string
  /// Привязка к месторождению. `null` — не привязано.
  fieldId:       number | null
  /// Датум пары; `''` — не выбран. Умолчания нет: пара без объявленного датума не точка.
  datum:         DatumId | ''
  /// Координаты якоря как строки — текстовое поле, парсим на submit.
  /// Обе пусты = якоря нет; одна пуста = ошибка (пара едет целиком).
  latitude_deg:  string
  longitude_deg: string
  /// Якорь задан в СЕТКЕ (метры): форма его не правит, здесь — подпись для показа. `null` — не так.
  grid_summary:  string | null
}

function emptyEditor(): EditorState {
  return {
    source:        null,
    name:          '',
    description:   '',
    fieldId:       null,
    datum:         '',
    latitude_deg:  '',
    longitude_deg: '',
    grid_summary:  null,
  }
}

// Пара в объявленном датуме — единственная форма якоря, которую правит эта страница.
type GeodeticAnchor = Extract<AnchorDto, { datum: DatumId }>
const isGeodetic = (a: AnchorDto): a is GeodeticAnchor => 'datum' in a

// Подпись якоря в сетке: «WGS84 / UTM zone 36N · N=… E=…». Сетка у куста — поле `crs` рядом с якорем.
const gridSummary = (a: Exclude<AnchorDto, GeodeticAnchor>, crs: DeclaredCrsDto | null): string => {
  const name = crs
    ? CRS_FAMILY_FULL_NAME_TEMPLATES[crs.family].replace('{zone}', String(crs.zone ?? '?'))
    : 'CRS не объявлена'
  return `${name} · N=${a.northing_m} E=${a.easting_m}`
}

// Якорь одной строкой: пара с датумом либо сетка с метрами. «—» = якоря нет вовсе (законное
// состояние: площадка заведена, на Земле ещё не привязана).
const anchorSummary = (c: ClusterDto): string => {
  const a = c.reference
  if (a == null) return '—'
  return isGeodetic(a)
    ? `${a.latitude_deg}, ${a.longitude_deg} · ${DATUM_NAMES[a.datum]}`
    : gridSummary(a, c.crs)
}

function editorFrom(c: ClusterDto): EditorState {
  const a = c.reference
  const pair = a && isGeodetic(a) ? a : null
  return {
    source:        c,
    name:          c.name,
    description:   c.description,
    fieldId:       c.field_id,
    datum:         pair?.datum ?? '',
    latitude_deg:  pair ? String(pair.latitude_deg) : '',
    longitude_deg: pair ? String(pair.longitude_deg) : '',
    grid_summary:  a && !isGeodetic(a) ? gridSummary(a, c.crs) : null,
  }
}

type EditorProps = {
  state:   EditorState
  fields:  FieldDto[]
  onClose: () => void
  onSaved: () => void
}

const Editor: Component<EditorProps> = (props) => {
  const initial = props.state
  const [st, setSt] = createSignal<EditorState>(props.state)
  const [busy, setBusy] = createSignal(false)
  const [err, setErr] = createSignal<string | null>(null)

  const isCreate = () => st().source === null

  const isDirty = createMemo<boolean>(() => {
    const c = st()
    if (isCreate()) {
      return (
        c.name.trim().length > 0 ||
        c.description.trim().length > 0 ||
        c.fieldId != null ||
        c.datum !== '' ||
        c.latitude_deg.trim().length > 0 ||
        c.longitude_deg.trim().length > 0
      )
    }
    return (
      c.name !== initial.name
      || c.description !== initial.description
      || c.fieldId !== initial.fieldId
      || anchorTouched(c)
    )
  })

  // Пара тронута. Якорь в сетке форме недоступен и всегда «не тронут».
  function anchorTouched(c: EditorState): boolean {
    return c.grid_summary === null && (
      c.datum !== initial.datum
      || c.latitude_deg !== initial.latitude_deg
      || c.longitude_deg !== initial.longitude_deg
    )
  }

  const tryClose = () => {
    if (busy()) return
    if (isDirty() && !confirm('Несохранённые изменения будут потеряны. Закрыть?')) return
    props.onClose()
  }

  onMount(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        tryClose()
      }
    }
    window.addEventListener('keydown', onKey)
    onCleanup(() => window.removeEventListener('keydown', onKey))
  })

  // Число в диапазоне [lo, hi]; пусто → null. Возвращает `number | null` при
  // успехе, либо строку-сообщение об ошибке.
  const parseNum = (raw: string, label: string, lo: number, hi: number): number | null | string => {
    const trimmed = raw.trim()
    if (trimmed === '') return null
    const n = Number(trimmed)
    if (!Number.isFinite(n)) return `Поле ${label}: ожидается число`
    if (n < lo || n > hi) return `Поле ${label}: вне диапазона [${lo}, ${hi}]`
    return n
  }

  // Собрать якорь из формы. Возвращает `AnchorDto | null` при успехе, либо
  // строку-сообщение. `null` здесь означает «пара пуста» — но что это значит
  // для запроса, решает вызывающий: у создания это «без якоря», у правки —
  // «не трогать» (снять якорь v1 не умеет, см. шапку файла).
  const buildAnchor = (c: EditorState): AnchorDto | null | string => {
    const latRaw = c.latitude_deg.trim()
    const lonRaw = c.longitude_deg.trim()
    if (latRaw === '' && lonRaw === '') {
      return c.datum === ''
        ? null
        : 'Датум выбран, а координат нет: введите широту и долготу или снимите датум'
    }
    if (latRaw === '' || lonRaw === '') {
      return 'Якорь задаётся парой: нужны и широта, и долгота (либо обе пустые)'
    }

    const lat = parseNum(latRaw, 'широта якоря', -90, 90)
    if (typeof lat === 'string') return lat
    const lon = parseNum(lonRaw, 'долгота якоря', -180, 180)
    if (typeof lon === 'string') return lon

    if (c.datum === '') return 'Выберите датум координат — без него пара не точка'

    return {
      datum:         c.datum,
      latitude_deg:  lat!,
      longitude_deg: lon!,
    }
  }

  const onSubmit = async (e: Event) => {
    e.preventDefault()
    setErr(null)
    const cur = st()
    const nameTrimmed = cur.name.trim()
    if (nameTrimmed.length === 0) {
      setErr('Название обязательно')
      return
    }

    // Якорь в сетке форма не собирает — он не тронут и не уезжает.
    const anchor = cur.grid_summary === null ? buildAnchor(cur) : null
    if (typeof anchor === 'string') { setErr(anchor); return }

    // Очистка координат у записи, где якорь БЫЛ — отказ, а не тихий no-op:
    // сервер прочитал бы `reference: null` как «не трогать» и оставил старый
    // ноль, а человек считал бы, что снял его.
    if (!isCreate() && anchorTouched(cur) && anchor === null && cur.source!.reference != null) {
      setErr('Снять уже заданный якорь через сайт нельзя — можно только изменить его координаты')
      return
    }

    setBusy(true)
    try {
      if (isCreate()) {
        const body: CreateClusterBody = {
          name:        nameTrimmed,
          description: cur.description,
          field_id:    cur.fieldId,
          reference:   anchor,
          // Сетка — только у якоря в метрах; пару форма шлёт без неё (одна CRS на точку).
          crs:         null,
        }
        await createCluster(body)
      } else {
        const body: UpdateClusterBody = {
          name:        nameTrimmed,
          description: cur.description,
          field_id:    cur.fieldId,
          // fieldId === null → отвязать месторождение (clear_field=true; сервер
          // обнулит колонку, проигнорировав field_id). Ровно тот же паттерн,
          // что у скважины: `None` у nullable-колонки означает «не трогать», и
          // отличить его от «обнулить» одним опционалом нечем. До 2026-09-01
          // флага в публичном API не было вовсе, и очистка пикера здесь молча
          // не сохранялась.
          clear_field: cur.fieldId === null,
          // `reference: null` = «не трогать» (см. шапку). Случай «был якорь,
          // стало пусто» сюда не доходит — отсечён выше. Нетронутый якорь не
          // пересылается — PATCH несёт только тронутое, как у месторождения.
          reference:   anchorTouched(cur) ? anchor : null,
          // `crs` без `reference` сервер отвергает (400), а у пары сетки нет.
          crs:         null,
        }
        await updateCluster(cur.source!.id, body)
      }
      props.onSaved()
    } catch (e) {
      setErr(String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form class="equipment-editor" onSubmit={onSubmit}>
      <div class="equipment-editor-head">
        <button class="btn btn-ghost" type="button" onClick={() => tryClose()} disabled={busy()}>
          ← К каталогу
        </button>
        <div class="equipment-editor-title">
          <div class="uppercase">{isCreate() ? 'Новая площадка' : 'Редактирование'}</div>
          <h1>{isCreate() ? 'Создать площадку' : st().name || 'Площадка'}</h1>
        </div>
        <div class="equipment-editor-actions">
          <button class="btn" type="button" onClick={() => tryClose()} disabled={busy()}>
            Отмена
          </button>
          <button class="btn btn-primary" type="submit" disabled={busy() || !isDirty()}>
            {busy() ? 'Сохраняем…' : isCreate() ? 'Создать' : 'Сохранить'}
          </button>
        </div>
      </div>

      <Show when={err()}>
        <div class="card equipment-error">{err()}</div>
      </Show>

      <main class="equipment-form-panel">
        <section class="equipment-form-section">
          <div class="equipment-form-main">
            <label class="field">
              <div class="field-label">Название</div>
              <input
                class="field-input"
                value={st().name}
                onInput={(e) => setSt({ ...st(), name: e.currentTarget.value })}
                placeholder="Напр., куст 12"
                autofocus
              />
            </label>

            <label class="field">
              <div class="field-label">Описание</div>
              <textarea
                class="field-input"
                rows={3}
                value={st().description}
                onInput={(e) => setSt({ ...st(), description: e.currentTarget.value })}
                placeholder="Произвольное примечание"
              />
            </label>

            <label class="field">
              <div class="field-label">Месторождение</div>
              <select
                class="field-input"
                value={st().fieldId == null ? '' : String(st().fieldId)}
                onChange={(e) => {
                  const v = e.currentTarget.value
                  setSt({ ...st(), fieldId: v === '' ? null : Number(v) })
                }}
              >
                <option value="">— не привязано —</option>
                <For each={props.fields}>
                  {(f) => <option value={String(f.id)}>{f.name}</option>}
                </For>
              </select>
            </label>
          </div>

          <div class="field">
            <div class="field-label">Якорь — центр площадки</div>
            <p class="muted text-xs" style={{ 'margin': '0 0 var(--sp-3)' }}>
              Пара задаётся целиком (обе координаты) и вместе со своим датумом;
              оставьте всё пустым, если привязки на Земле пока нет.
            </p>
            <Show when={st().grid_summary}>
              {(g) => (
                <div class="muted text-sm" style={{ 'margin-bottom': 'var(--sp-2)' }}>
                  Якорь задан в сетке: {g()}. Здесь он не правится — вводится в приложении.
                </div>
              )}
            </Show>
            <div class="dim-grid equipment-dim-grid">
              <div class="dim-cell">
                <div class="dim-label">
                  <span class="dim-symbol">Датум</span>
                  <span class="dim-hint">система координат пары</span>
                </div>
                <select
                  class="field-input"
                  value={st().datum}
                  disabled={st().grid_summary !== null}
                  onChange={(e) => setSt({ ...st(), datum: e.currentTarget.value as DatumId | '' })}
                >
                  <option value="">— не выбран —</option>
                  <For each={DATUM_IDS}>
                    {(d) => <option value={d}>{DATUM_NAMES[d]}</option>}
                  </For>
                </select>
              </div>

              <div class="dim-cell">
                <div class="dim-label">
                  <span class="dim-symbol">Широта, °</span>
                  <span class="dim-hint">якорь, [-90; 90]</span>
                </div>
                <input
                  class="field-input mono"
                  disabled={st().grid_summary !== null}
                  value={st().latitude_deg}
                  onInput={(e) => setSt({ ...st(), latitude_deg: e.currentTarget.value })}
                  inputmode="decimal"
                  placeholder="61.05"
                />
              </div>

              <div class="dim-cell">
                <div class="dim-label">
                  <span class="dim-symbol">Долгота, °</span>
                  <span class="dim-hint">якорь, [-180; 180]</span>
                </div>
                <input
                  class="field-input mono"
                  disabled={st().grid_summary !== null}
                  value={st().longitude_deg}
                  onInput={(e) => setSt({ ...st(), longitude_deg: e.currentTarget.value })}
                  inputmode="decimal"
                  placeholder="76.8"
                />
              </div>
            </div>
          </div>
        </section>
      </main>
    </form>
  )
}

// ────────────────────── страница ──────────────────────

type ClustersPageProps = {
  clusterId?: number | null
  create?:    boolean
  onOpen:     (id: number) => void
  onCreate:   () => void
  onBack:     () => void
}

export const ClustersPage: Component<ClustersPageProps> = (props) => {
  const [items, { refetch }] = createResource(() => listClusters())
  const pageItems = createMemo<ClusterDto[]>(() => items()?.items ?? [])
  // Каталог месторождений — для селектора привязки и для колонки в таблице.
  const [fields] = createResource(() => listFields({ limit: 200 }))
  const [delErr, setDelErr] = createSignal<string | null>(null)
  const [filterText, setFilterText] = createSignal('')

  const fieldList = createMemo<FieldDto[]>(() => fields()?.items ?? [])
  const fieldNameById = createMemo<Record<number, string>>(() => {
    const map: Record<number, string> = {}
    for (const f of fieldList()) map[f.id] = f.name
    return map
  })

  const editor = createMemo<EditorState | null>(() => {
    if (props.create) return emptyEditor()
    if (props.clusterId == null) return null
    const item = pageItems().find((c) => c.id === props.clusterId)
    return item ? editorFrom(item) : null
  })

  const filtered = createMemo<ClusterDto[]>(() => {
    const all = pageItems()
    const q = filterText().trim().toLowerCase()
    if (q.length === 0) return all
    const names = fieldNameById()
    return all.filter((c) =>
      c.name.toLowerCase().includes(q)
      || (c.description ?? '').toLowerCase().includes(q)
      || (c.field_id != null && (names[c.field_id] ?? '').toLowerCase().includes(q))
    )
  })

  const openCreate = () => props.onCreate()
  const openEdit = (c: ClusterDto) => props.onOpen(c.id)
  const closeEditor = () => props.onBack()
  const onSaved = () => {
    refetch()
    props.onBack()
  }

  const onDelete = async (c: ClusterDto) => {
    if (!confirm(`Удалить «${c.name}»?`)) return
    setDelErr(null)
    try {
      await deleteCluster(c.id)
      refetch()
    } catch (err) {
      if (err instanceof InUseError) setDelErr(err.message)
      else setDelErr(String(err))
    }
  }

  const fieldSummary = (c: ClusterDto): string =>
    c.field_id == null ? '—' : (fieldNameById()[c.field_id] ?? `#${c.field_id}`)

  return (
    <Show
      when={editor()}
      keyed
      fallback={<div class="page page-wide">
      <div class="page-header">
        <div>
          <h1 class="page-title">Каталог площадок</h1>
          <p class="page-subtitle">
            Площадки (кусты) — уровень между месторождением и скважиной.
            Якорь площадки — её центр на Земле, со своим датумом.
          </p>
        </div>
        <div class="row">
          <button class="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" />Площадка
          </button>
        </div>
      </div>

      <Show when={delErr()}>
        <div class="card" style={{ padding: 'var(--sp-3) var(--sp-4)', 'margin-bottom': 'var(--sp-4)', color: 'var(--status-err)' }}>
          {delErr()}
        </div>
      </Show>

      <Show when={items.loading}>
        <div class="card" style={{ padding: 'var(--sp-5)' }}>Загрузка…</div>
      </Show>

      <Show when={items.error}>
        <div class="card" style={{ padding: 'var(--sp-5)', color: 'var(--status-err)' }}>
          Ошибка: {String(items.error)}
        </div>
      </Show>

      <Show when={items() && items()!.total === 0}>
        <div class="card" style={{ padding: 'var(--sp-7)', 'text-align': 'center' }}>
          <div style={{ 'font-size': 'var(--fs-md)', 'margin-bottom': 'var(--sp-2)' }}>
            Каталог пуст
          </div>
          <div class="muted text-sm" style={{ 'margin-bottom': 'var(--sp-4)' }}>
            Создайте первую площадку
          </div>
          <button class="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" />Добавить площадку
          </button>
        </div>
      </Show>

      <Show when={items() && items()!.total > 0}>
        <div class="row filter-row" style={{ gap: 'var(--sp-3)', 'margin-bottom': 'var(--sp-4)' }}>
          <div class="input" style={{ flex: 1, 'max-width': '320px' }}>
            <Icon name="search" />
            <input
              placeholder="Поиск по названию / месторождению"
              value={filterText()}
              onInput={(e) => setFilterText(e.currentTarget.value)}
            />
            <Show when={filterText().length > 0}>
              <button
                class="btn-icon btn-ghost"
                style={{ width: '24px', height: '24px' }}
                title="Очистить"
                onClick={() => setFilterText('')}
              >
                <Icon name="x" size={12} />
              </button>
            </Show>
          </div>
          <div class="muted text-sm filter-counter" style={{ 'margin-left': 'auto', 'align-self': 'center' }}>
            Показано {filtered().length} из {items()!.total}
          </div>
        </div>

        <div class="card">
          <table class="tbl">
            <thead>
              <tr>
                <th style={{ width: '110px' }}>ID</th>
                <th>Название</th>
                <th>Месторождение</th>
                <th>Якорь</th>
                <th>Описание</th>
                <th style={{ width: '80px' }} />
              </tr>
            </thead>
            <tbody>
              <For each={filtered()}>
                {(c) => (
                  <tr style={{ cursor: 'pointer' }} onClick={() => openEdit(c)}>
                    <td class="mono text-xs muted">#{c.id}</td>
                    <td style={{ 'font-weight': 500 }}>{c.name}</td>
                    <td class="text-xs">{fieldSummary(c)}</td>
                    <td class="mono text-xs">{anchorSummary(c)}</td>
                    <td class="text-xs muted">{c.description || '—'}</td>
                    <td>
                      <button
                        class="btn-icon"
                        title="Удалить"
                        onClick={(ev) => {
                          ev.stopPropagation()
                          onDelete(c)
                        }}
                      >
                        <Icon name="x" />
                      </button>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
          <Show when={filtered().length === 0}>
            <div style={{ padding: 'var(--sp-5)', 'text-align': 'center' }} class="muted text-sm">
              Ничего не найдено по фильтру
            </div>
          </Show>
        </div>
      </Show>

      </div>}
    >
      {(ed) => (
        <div class="page page-wide">
          <Editor state={ed} fields={fieldList()} onClose={closeEditor} onSaved={onSaved} />
        </div>
      )}
    </Show>
  )
}
